import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, trackEvent } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  BookOpen,
  Target,
  TrendingUp,
  Clock,
  Award,
  ChevronRight,
  Users,
  Zap,
  BarChart3,
  GraduationCap,
  Briefcase,
  Building2,
  FileText,
  CheckCircle2,
  Play,
  MessageSquare,
  Mail,
  Shield,
  ArrowRight,
  Sparkles,
  Trophy,
} from "lucide-react";
import Navbar from "../components/Navbar";

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [featuredPassage, setFeaturedPassage] = useState(null);

  // Track page view
  useEffect(() => {
    trackEvent("page_view", "/", { page_type: "landing" });
  }, []);

  // Fetch a featured passage for preview
  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API}/trial/passages`);
        if (response.data.passages?.length > 0) {
          // Pick a random passage from trial set
          const passages = response.data.passages;
          setFeaturedPassage(passages[Math.floor(Math.random() * passages.length)]);
        }
      } catch (error) {
        console.error("Error fetching featured passage:", error);
      }
    };
    fetchFeatured();
  }, []);

  const navigateToPractice = () => {
    trackEvent("cta_click", "/", { button: "start_practice" });
    if (isAuthenticated) {
      navigate("/practice");
    } else {
      navigate("/trial");
    }
  };

  const audienceBadges = [
    { icon: GraduationCap, label: "For School Students", color: "bg-sky-100 text-sky-700" },
    { icon: BookOpen, label: "For College Practice", color: "bg-violet-100 text-violet-700" },
    { icon: Briefcase, label: "For Competitive Exams", color: "bg-amber-100 text-amber-700" },
    { icon: FileText, label: "UPSC Editorial Level", color: "bg-rose-100 text-rose-700" },
    { icon: Trophy, label: "Daily Reading Habit", color: "bg-emerald-100 text-emerald-700" },
  ];

  const howItWorks = [
    {
      step: "01",
      icon: BookOpen,
      title: "Read Passage",
      desc: "Carefully read curated passages from various categories and difficulty levels",
    },
    {
      step: "02",
      icon: Target,
      title: "Answer Questions",
      desc: "Test your comprehension with MCQs on inference, vocabulary, and analysis",
    },
    {
      step: "03",
      icon: BarChart3,
      title: "Get Feedback",
      desc: "View detailed explanations, track progress, and improve with insights",
    },
  ];

  const stats = [
    { value: "1000+", label: "Passages", icon: BookOpen },
    { value: "5000+", label: "Questions", icon: Target },
    { value: "6", label: "Practice Modes", icon: GraduationCap },
    { value: "5", label: "Difficulty Levels", icon: TrendingUp },
  ];

  const modes = [
    { id: "school", name: "School", desc: "Beginner level for students", color: "bg-sky-500" },
    { id: "college", name: "College", desc: "Intermediate academic level", color: "bg-violet-500" },
    { id: "general", name: "General", desc: "Everyday English practice", color: "bg-slate-500" },
    { id: "ssc", name: "SSC", desc: "Staff Selection Commission", color: "bg-amber-500" },
    { id: "banking", name: "Banking", desc: "Bank exam preparation", color: "bg-emerald-500" },
    { id: "upsc", name: "UPSC", desc: "Civil Services preparation", color: "bg-rose-500" },
  ];

  const features = [
    { icon: BookOpen, title: "Curated Passages", desc: "Editorials, news, stories & biographies" },
    { icon: Target, title: "Exam-Focused Questions", desc: "Inference, vocabulary, tone & arguments" },
    { icon: TrendingUp, title: "Track Progress", desc: "Detailed analytics and insights" },
    { icon: Clock, title: "Timed Practice", desc: "Simulate real exam conditions" },
    { icon: Award, title: "Streak Rewards", desc: "Stay motivated with daily streaks" },
    { icon: BarChart3, title: "Smart Insights", desc: "AI-powered recommendations" },
  ];

  const difficultyLabels = { 1: "Beginner", 2: "Intermediate", 3: "Advanced", 4: "Editorial" };
  const difficultyColors = {
    1: "bg-emerald-100 text-emerald-700",
    2: "bg-blue-100 text-blue-700",
    3: "bg-amber-100 text-amber-700",
    4: "bg-purple-100 text-purple-700",
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              {/* Audience Badges */}
              <div className="flex flex-wrap gap-2">
                {audienceBadges.slice(0, 4).map((badge, idx) => (
                  <span
                    key={idx}
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${badge.color}`}
                  >
                    <badge.icon className="w-3 h-3" />
                    {badge.label}
                  </span>
                ))}
              </div>

              {/* Headline */}
              <div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.1]">
                  Master Reading
                  <br />
                  <span className="bg-gradient-to-r from-sky-600 to-indigo-600 bg-clip-text text-transparent">
                    Comprehension
                  </span>
                </h1>
              </div>

              {/* Subtitle */}
              <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                Build your reading speed, accuracy, and analytical skills with practice passages designed for School, College, SSC, Banking, and UPSC exams.
              </p>

              {/* CTAs */}
              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  data-testid="start-practicing-btn"
                  onClick={navigateToPractice}
                  className="h-12 px-6 text-base rounded-xl bg-slate-900 hover:bg-slate-800 shadow-lg shadow-slate-900/20"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Free Trial (6 Passages)
                </Button>
                <Button
                  data-testid="login-btn"
                  variant="outline"
                  onClick={() => navigate(isAuthenticated ? "/dashboard" : "/auth")}
                  className="h-12 px-6 text-base rounded-xl border-slate-300"
                >
                  {isAuthenticated ? "View Dashboard" : "Login"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>

              {/* Trust Line */}
              <div className="flex items-center gap-2 text-sm text-slate-500 pt-1">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>No login required for trial</span>
                <span className="text-slate-300">•</span>
                <span>Instant results</span>
                <span className="text-slate-300">•</span>
                <span>100% Free</span>
              </div>

              {/* Social Proof */}
              <div className="flex items-center gap-4 pt-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white flex items-center justify-center text-xs font-medium text-slate-600"
                    >
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">10,000+</span> students practicing daily
                </p>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-slide-up hidden lg:block">
              <div className="relative z-10">
                <img
                  src="https://images.unsplash.com/photo-1767102060241-130cb9260718?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxmb2N1c2VkJTIwc3R1ZGVudCUyMHN0dWR5aW5nJTIwbGlicmFyeXxlbnwwfHx8fDE3NjkyMzEwMTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Student studying"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
              </div>
              {/* Floating Stats Card */}
              <div className="absolute -bottom-4 -left-4 bg-white rounded-xl p-4 shadow-xl border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Avg. Improvement</p>
                    <p className="text-xl font-bold text-slate-900">+35%</p>
                  </div>
                </div>
              </div>
              {/* Floating Badge */}
              <div className="absolute top-4 -right-2 bg-white rounded-lg px-3 py-2 shadow-lg border border-slate-100">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-slate-700">AI-Powered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                  <stat.icon className="w-6 h-6 text-slate-700" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-slate-500">
            Built for students, exam aspirants, and everyday learners to improve reading comprehension.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-sky-100 text-sky-700">Simple & Effective</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-base text-slate-600 max-w-xl mx-auto">
              Start improving your reading comprehension in just 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((item, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-black text-slate-200">{item.step}</span>
                    <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
                {idx < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Passage Preview */}
      {featuredPassage && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="mb-4 bg-amber-100 text-amber-700">Try It Now</Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Sample Passage Preview
              </h2>
              <p className="text-base text-slate-600">
                Here's a taste of what you'll practice. Click to start your free trial.
              </p>
            </div>

            <Card
              className="cursor-pointer card-hover border-2 border-slate-200 hover:border-sky-300 transition-all"
              onClick={navigateToPractice}
              data-testid="featured-passage-card"
            >
              <CardContent className="p-8">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <Badge className={difficultyColors[featuredPassage.difficulty]}>
                    {difficultyLabels[featuredPassage.difficulty]}
                  </Badge>
                  <Badge variant="outline" className="capitalize">
                    {featuredPassage.category}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    {featuredPassage.question_count} questions
                  </span>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-4">{featuredPassage.title}</h3>

                <p className="text-slate-600 leading-relaxed mb-6 line-clamp-4">
                  {featuredPassage.content?.substring(0, 300)}...
                </p>

                <Button className="bg-slate-900 hover:bg-slate-800">
                  <Play className="w-4 h-4 mr-2" />
                  Try This Passage
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Exam Modes Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-violet-100 text-violet-700">Tailored Content</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choose Your Exam Mode
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Practice with passages and questions tailored to your target exam
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {modes.map((mode, idx) => (
              <div
                key={mode.id}
                data-testid={`mode-card-${mode.id}`}
                onClick={() => {
                  if (isAuthenticated) {
                    navigate(`/practice?mode=${mode.id}`);
                  } else {
                    navigate("/trial");
                  }
                }}
                className="mode-card group animate-fade-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className={`w-12 h-12 rounded-xl ${mode.color} flex items-center justify-center mb-4`}>
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 group-hover:text-sky-600 transition-colors">
                  {mode.name}
                </h3>
                <p className="text-slate-600">{mode.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700">Full Toolkit</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Everything You Need to Excel
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Comprehensive tools designed to help you master reading comprehension
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div
                key={feature.title}
                className="bg-slate-50 rounded-xl p-6 border border-slate-100 card-hover animate-fade-in"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-sky-300 text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            Start your journey today
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Improve Your Reading Skills?
          </h2>
          <p className="text-base text-slate-400 mb-8 max-w-2xl mx-auto">
            Join thousands of students who are already practicing daily and seeing real improvements in their comprehension scores.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              data-testid="cta-start-btn"
              onClick={navigateToPractice}
              className="h-12 px-8 text-base rounded-xl bg-white text-slate-900 hover:bg-slate-100 shadow-lg"
            >
              <Play className="w-4 h-4 mr-2" />
              Start Free Trial
            </Button>
            {!isAuthenticated && (
              <Button
                data-testid="cta-signup-btn"
                variant="outline"
                onClick={() => navigate("/auth")}
                className="h-12 px-8 text-base rounded-xl border-slate-600 text-white hover:bg-white/10"
              >
                Create Free Account
              </Button>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-6">
            No credit card required • 6 free passages • Instant access
          </p>
        </div>
      </section>

      {/* Professional Footer */}
      <footer className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="w-6 h-6 text-sky-500" />
                <span className="text-xl font-bold text-white">ReadMaster</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Build reading comprehension daily. For students, aspirants, and lifelong learners.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={navigateToPractice} className="text-sm text-slate-400 hover:text-white transition-colors">
                    Practice
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate("/trial")} className="text-sm text-slate-400 hover:text-white transition-colors">
                    Free Trial
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate(isAuthenticated ? "/dashboard" : "/auth")} className="text-sm text-slate-400 hover:text-white transition-colors">
                    Dashboard
                  </button>
                </li>
              </ul>
            </div>

            {/* Exam Modes */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Exam Modes</h4>
              <ul className="space-y-2">
                {["School", "College", "SSC", "Banking", "UPSC"].map((mode) => (
                  <li key={mode}>
                    <span className="text-sm text-slate-400">{mode}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-slate-400">About</span></li>
                <li><span className="text-sm text-slate-400">FAQ</span></li>
                <li><span className="text-sm text-slate-400">Contact</span></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><span className="text-sm text-slate-400">Privacy Policy</span></li>
                <li><span className="text-sm text-slate-400">Terms of Service</span></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © 2025 ReadMaster — Build reading comprehension daily.
            </p>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-500">Made with ❤️ for learners</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
